import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface RootErrorBoundaryFallbackProps {
  error?: Error | null;
}

export function RootErrorBoundaryFallback({ error }: RootErrorBoundaryFallbackProps) {
  const { t } = useTranslation(['common']);
  const [copied, setCopied] = useState(false);

  const handleCopyError = () => {
    const errorInfo = `URL: ${window.location.href}
Error: ${error?.message || 'N/A'}
Stack: ${error?.stack || 'N/A'}`;
    navigator.clipboard.writeText(errorInfo).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy error info:', err);
    });
  };

  return (
    <div className="w-full h-screen flex flex-col items-center justify-center p-6 bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-red-500 dark:text-red-400 mb-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <h2 className="text-2xl font-bold text-red-600 dark:text-red-500 mb-3">
          {t('common:errors.applicationError')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-5">
          {t('common:errors.criticalErrorMessage')}
        </p>

        {error && (
          <div className="text-left bg-gray-50 dark:bg-gray-700 p-3 rounded-md mb-5 max-h-32 overflow-y-auto">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('common:errors.errorDetailsLabel')}:</p>
            <p className="text-xs text-red-500 dark:text-red-400 break-words">
              {error.message || t('common:errors.noErrorMessageAvailable')}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-3">
          <button
            className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg shadow-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors"
            onClick={() => window.location.reload()}
          >
            {t('common:actions.refreshPage')}
          </button>
          <button
            className={`w-full sm:w-auto px-6 py-2.5 font-medium text-sm rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors ${copied ? 'bg-green-500 text-white hover:bg-green-600 focus:ring-green-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500 focus:ring-gray-500'}`}
            onClick={handleCopyError}
          >
            {copied ? t('common:actions.copied') : t('common:actions.copyErrorInfo')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RootErrorBoundaryFallback;